import React, { createContext, useCallback, useContext, useMemo, useState, useEffect } from 'react';
import { isTokenValid } from './services/api';

const SessionCtx = createContext(null);

/**
 * Read persisted user from localStorage (single key: "user").
 * Only returns data if a valid JWT token also exists.
 */
function readPersistedUser() {
    if (!isTokenValid()) return null;
    try {
        const raw = localStorage.getItem('user');
        if (!raw) return null;
        const u = JSON.parse(raw);
        const userId = u.UserID ?? u.userId ?? '';
        const username = u.FullName ?? u.UserName ?? u.LoginName ?? u.username ?? u.name ?? '';
        const roleName = u.RoleName ?? u.roleName ?? '';
        if (!userId && !username) return null;
        return { userId, username, roleName };
    } catch {
        return null;
    }
}

export function SessionProvider({ children }) {
    const [user, setUserState] = useState(() => readPersistedUser());

    const setUser = useCallback((u) => {
        const normalized = {
            userId: Number.isNaN(Number(u?.userId)) ? String(u?.userId ?? '') : Number(u.userId),
            username: String(u?.username ?? ''),
            roleName: String(u?.roleName ?? ''),
        };
        setUserState(normalized);
    }, []);

    // If token expires while app is open, clear user
    useEffect(() => {
        if (user && !isTokenValid()) {
            setUserState(null);
        }
    }, [user]);

    const clearUser = useCallback(() => {
        setUserState(null);
    }, []);

    const isAuthenticated = useMemo(() => !!user && isTokenValid(), [user]);

    const value = useMemo(
        () => ({ user, setUser, clearUser, isAuthenticated }),
        [user, setUser, clearUser, isAuthenticated]
    );
    return <SessionCtx.Provider value={value}>{children}</SessionCtx.Provider>;
}

export function useSessionUser() {
    const ctx = useContext(SessionCtx);
    if (!ctx) throw new Error('useSessionUser must be used within <SessionProvider>');
    return ctx; // { user, setUser, clearUser, isAuthenticated }
}
